import { Component, EventEmitter, Input, Optional, Output, ViewChild, forwardRef } from '@angular/core';
import { ControlContainer, ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NgbTypeahead } from '@ng-bootstrap/ng-bootstrap';
import { Subject, OperatorFunction, Observable, merge, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';

import { SearchSelectorItem } from './models/search-selector-item';

@Component({
  selector: 'app-search-selector-v2',
  templateUrl: './search-selector-v2.component.html',
  styleUrls: ['./search-selector-v2.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SearchSelectorV2Component),
      multi: true,
    },
  ],
  standalone: false,
})
export class SearchSelectorV2Component implements ControlValueAccessor {
  @ViewChild('instance', { static: true }) instance: NgbTypeahead;

  @Input() vrijeInvulMogelijk = false;
  @Input() placeHolderValue: string;
  @Input() toonResetKnop = true;
  @Input() defaultValue: string = '';
  @Input() styleClass: string = '';
  @Input() searchFn?: (term: string) => Observable<SearchSelectorItem[]>;
  @Input() searchDebounceMs = 100;
  @Input() minimumSearchLength = 1;

  @Input() items: SearchSelectorItem[] = [];
  @Input() formControl: FormControl;
  @Input() formControlName: string;
  @Output() resetted = new EventEmitter();
  @Output() selected = new EventEmitter<SearchSelectorItem>();
  @Output() valueChanged = new EventEmitter<string>();

  // Maintain both the selected item and the display value
  selectedItem: SearchSelectorItem | null = null;
  displayValue: string = '';
  disabled = false;

  get isInvalidAndInteracted() {
    const control = this.control;
    return !!control && control.invalid && control.dirty && control.touched;
  }

  get control() {
    return this.formControl || this.controlContainer?.control?.get(this.formControlName);
  }

  onTouched: () => void = () => {};
  onChange: (value: any) => void = () => {};

  focus$ = new Subject<string>();
  click$ = new Subject<string>();

  constructor(@Optional() private controlContainer: ControlContainer) {}

  writeValue(value: any): void {
    if (value === null || value === undefined) {
      // Reset everything
      this.selectedItem = null;
      this.displayValue = this.defaultValue;
      return;
    }

    // If value is a primitive (identifier directly)
    if (typeof value !== 'object') {
      // Find the item with this identifier
      const matchingItem = this.items.find((item) => item.identifier === value);
      if (matchingItem) {
        this.selectedItem = matchingItem;
        this.displayValue = matchingItem.weergave;
      } else {
        // No matching item found - just use the value
        this.selectedItem = null;
        this.displayValue = String(value);
      }
    }
    // If it's a SearchSelectorItem object
    else if (value.hasOwnProperty('weergave') && value.hasOwnProperty('identifier')) {
      this.selectedItem = value;
      this.displayValue = value.weergave;
    }
    // Other object, try to handle gracefully
    else {
      this.selectedItem = null;
      this.displayValue = String(value);
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  reset() {
    this.formControl.reset();
    this.selectedItem = null;
    this.displayValue = this.defaultValue;
    this.onChange(null);
    this.resetted.emit();
  }

  selectItem(event: any) {
    this.selectedItem = event.item;
    this.displayValue = event.item.weergave;

    // Pass the identifier to the form control for standard selectors, or preserve selected item / display
    this.onChange(event.item.identifier);
    this.onTouched();
    this.selected.emit(event.item);
  }

  displayValueChanged(value: any): void {
    if (value && typeof value === 'object' && value.identifier !== undefined) {
      this.selectedItem = value;
      this.displayValue = value.weergave ?? '';
      // When selected via dropdown, selectItem already handled onChange.
      // Do not re-emit value.identifier to avoid clobbering parent form control listeners or composite models.
      return;
    }

    this.displayValue = value ?? '';
    if (this.vrijeInvulMogelijk) {
      this.selectedItem = null;
      this.onChange(this.displayValue);
      this.valueChanged.emit(this.displayValue);
    }
  }

  search: OperatorFunction<string, readonly SearchSelectorItem[]> = (text$: Observable<string>) => {
    const debouncedText$ = text$.pipe(debounceTime(this.searchFn ? this.searchDebounceMs : 100), distinctUntilChanged());
    const clicksWithClosedPopup$ = this.click$.pipe(filter(() => !this.instance?.isPopupOpen()));
    const inputFocus$ = this.focus$;

    return merge(inputFocus$, debouncedText$, clicksWithClosedPopup$).pipe(
      switchMap((term) => {
        const normalizedTerm = `${term ?? ''}`.trim();

        if (this.searchFn) {
          if (normalizedTerm.length < this.minimumSearchLength) {
            this.items = [];
            return of([]);
          }

          return this.searchFn(normalizedTerm).pipe(
            map((items) => {
              this.items = items ?? [];
              return this.items;
            }),
            catchError(() => {
              this.items = [];
              return of([]);
            })
          );
        }

        let items = this.items;
        if (normalizedTerm.length > 0) {
          items = items?.filter((item) => item.weergave.toLowerCase().indexOf(normalizedTerm.toLowerCase()) > -1);
        }
        if (normalizedTerm.length <= 2) {
          items = items?.slice(0, 150); // Slice array for performance
        }
        return of(items);
      })
    );
  };

  // This formatter is used for display in the dropdown
  formatter = (item: SearchSelectorItem) => item.weergave;

  // This formatter is used for the input field text
  inputFormatter = (value: any): string => {
    if (!value) return '';

    if (typeof value === 'object' && value.weergave) {
      return value.weergave;
    }

    // Try to find a matching item for the identifier
    if (this.items && this.items.length > 0) {
      const item = this.items.find((i) => i.identifier === value);
      if (item) return item.weergave;
    }

    return String(value);
  };
}
